import { useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import axiosInstance from "@/lib/axios";
import { callLogEndpoint } from "@/config/endpoints";

/**
 * Inline call-recording player for an activity-feed row.
 *
 * The feed only knows THAT a call has a recording; the URL is fetched on the
 * first Play from `/call-logs/:id/recording-url`, which applies the call read
 * scope (own calls, branch peers, calls on leads you can open) and resolves
 * provider URLs (TeleCMI). Nothing is downloaded for rows nobody plays — a
 * page of 50 rows with 50 waveform players would pull 50 audio files.
 *
 * One recording plays at a time across the whole feed.
 */

let activeAudio: HTMLAudioElement | null = null;
const urlCache = new Map<number, string>();

function clock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function CallRecordingPlayer({
  callLogId,
  fallbackDurationSec,
}: {
  callLogId: number;
  /** Talk time from the call log, shown before the audio's own length loads. */
  fallbackDurationSec: number;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "playing" | "paused" | "error">("idle");
  const [position, setPosition] = useState(0);
  const [length, setLength] = useState(fallbackDurationSec);

  useEffect(
    () => () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        if (activeAudio === audio) activeAudio = null;
      }
    },
    [],
  );

  const ensureAudio = async (): Promise<HTMLAudioElement | null> => {
    if (audioRef.current) return audioRef.current;
    let url = urlCache.get(callLogId);
    if (!url) {
      const res = await axiosInstance.get(`${callLogEndpoint}/${callLogId}/recording-url`);
      url = res.data?.recordingUrl ?? undefined;
      if (!url) return null;
      urlCache.set(callLogId, url);
    }
    const audio = new Audio(url);
    audio.preload = "metadata";
    audio.addEventListener("loadedmetadata", () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) setLength(audio.duration);
    });
    audio.addEventListener("timeupdate", () => setPosition(audio.currentTime));
    audio.addEventListener("play", () => setState("playing"));
    audio.addEventListener("pause", () => setState((s) => (s === "error" ? s : "paused")));
    audio.addEventListener("ended", () => {
      setState("paused");
      setPosition(0);
      audio.currentTime = 0;
    });
    audio.addEventListener("error", () => setState("error"));
    audioRef.current = audio;
    return audio;
  };

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (state === "playing") {
      audioRef.current?.pause();
      return;
    }
    try {
      if (!audioRef.current) setState("loading");
      const audio = await ensureAudio();
      if (!audio) {
        setState("error");
        return;
      }
      if (activeAudio && activeAudio !== audio) activeAudio.pause();
      activeAudio = audio;
      await audio.play();
    } catch {
      setState("error");
    }
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio || !length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * length;
    setPosition(audio.currentTime);
  };

  if (state === "error") {
    return (
      <span
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center rounded-full bg-[#f1f4f3] px-2.5 py-1 text-[11px] text-[#697571]"
      >
        Recording unavailable
      </span>
    );
  }

  const started = state === "playing" || state === "paused";
  const pct = length > 0 ? Math.min(100, (position / length) * 100) : 0;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="inline-flex w-full max-w-[340px] items-center gap-2.5 rounded-full border border-[#d6ebe3] bg-[#f3faf7] py-1 pl-1 pr-3"
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={state === "playing" ? "Pause recording" : "Play recording"}
        className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#156548] text-white transition-colors duration-150 hover:bg-[#0f4d37] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#156548]"
      >
        {state === "loading" ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : state === "playing" ? (
          <Pause className="size-3.5" fill="currentColor" />
        ) : (
          <Play className="size-3.5 translate-x-px" fill="currentColor" />
        )}
      </button>

      {started ? (
        <>
          <div
            role="slider"
            aria-label="Seek recording"
            aria-valuemin={0}
            aria-valuemax={Math.round(length)}
            aria-valuenow={Math.round(position)}
            onClick={seek}
            className="relative h-1.5 flex-1 cursor-pointer rounded-full bg-[#d6ebe3]"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-[#156548]"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="shrink-0 tabular-nums text-[11px] text-[#374741]">
            {clock(position)} / {clock(length)}
          </span>
        </>
      ) : (
        <span className="text-[12px] font-medium tracking-[-0.24px] text-[#156548]">
          {state === "loading" ? "Loading recording…" : "Play recording"}
          {state !== "loading" && length > 0 && (
            <span className="ml-1.5 font-normal text-[#697571]">{clock(length)}</span>
          )}
        </span>
      )}
    </div>
  );
}
